import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Smartphone, Download, Send, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { enablePush, isIOS, isStandalone, pushSupported } from "../lib/push";
import { pushTest, runAlertsNow, updateNotificationSettings } from "../lib/api";

const PREFS = [["push_watch", "Watchlist buy / sell hits"], ["push_intraday", "Intraday opportunities"], ["push_daily", "8:30 AM top picks & rescans"]];

const Btn = ({ children, ...p }) => (
  <button {...p} className="flex items-center justify-center gap-1.5 rounded-lg border border-[#2d3c59] px-3 py-2 text-xs text-[#cbd5e1] hover:border-[#00e5ff]/60 hover:text-[#00e5ff] transition-colors disabled:opacity-50">{children}</button>
);

export default function PhoneSetup({ devices, settings }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState("");
  const refresh = () => qc.invalidateQueries({ queryKey: ["notifications"] });
  const act = (name, fn) => async () => {
    setBusy(name);
    try { await fn(); } catch (e) { toast.error(e?.response?.data?.detail || e.message); } finally { setBusy(""); refresh(); }
  };
  const ios = isIOS() && !isStandalone();
  const canInstall = !isStandalone() && window.__installPrompt;

  return (
    <div className="rounded-xl border border-[#1e293b] p-4 space-y-3" data-testid="phone-setup">
      <div className="flex items-center gap-2 text-sm text-[#f8fafc]"><Smartphone className="w-4 h-4 text-[#00e5ff]" /> Phone alerts</div>
      <div className="text-xs text-[#94a3b8]" data-testid="push-devices">
        {devices ? `${devices} device${devices > 1 ? "s" : ""} receiving push alerts.` : "No device enabled yet."}
        {ios && " On iPhone: tap Share → Add to Home Screen, then open Alpha from your home screen and enable alerts."}
        {!pushSupported() && !ios && " This browser can't receive push — use Chrome on Android or Safari (home screen) on iPhone."}
      </div>
      <div className="grid grid-cols-2 gap-2">
        {canInstall && <Btn data-testid="install-app-btn" onClick={act("install", async () => { await window.__installPrompt.prompt(); window.__installPrompt = null; })}><Download className="w-3.5 h-3.5" /> Install app</Btn>}
        <Btn data-testid="enable-push-btn" disabled={busy === "push"} onClick={act("push", async () => { await enablePush(); toast.success("Alerts enabled on this device"); })}><Smartphone className="w-3.5 h-3.5" /> {busy === "push" ? "Enabling…" : "Enable on this device"}</Btn>
        <Btn data-testid="push-test-btn" disabled={busy === "test"} onClick={act("test", async () => { const r = await pushTest(); toast.success(`Test sent to ${r.delivered} device(s)`); })}><Send className="w-3.5 h-3.5" /> Send test</Btn>
        <Btn data-testid="alerts-run-now-btn" disabled={busy === "run"} onClick={act("run", async () => { const r = await runAlertsNow({ ignore_hours: true }); toast.success(`Checked ${r.checked} prices · ${r.watch_events} watch hits · ${r.intraday} intraday`); })}><RefreshCw className={`w-3.5 h-3.5 ${busy === "run" ? "animate-spin" : ""}`} /> Check prices now</Btn>
      </div>
      <div className="space-y-2 pt-1">
        {PREFS.map(([k, l]) => {
          const on = settings[k] !== false;
          return (
            <button key={k} data-testid={`pref-${k}`} onClick={act(k, () => updateNotificationSettings({ [k]: !on }))} className="flex items-center gap-2 w-full">
              <span className="relative w-9 h-5 rounded-full transition-colors shrink-0" style={{ background: on ? "#00e5ff" : "#2d3c59" }}>
                <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all" style={{ left: on ? "18px" : "2px" }} />
              </span>
              <span className="text-xs text-[#94a3b8]">{l}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
